import React, { useEffect, useMemo, useState } from 'react';
import API from '../services/api';
import {
  Search,
  RefreshCw,
  X,
  ChevronRight,
  Filter,
  Calendar,
  CreditCard,
  User,
  Save,
  DollarSign,
  CheckCircle
} from 'lucide-react';

const PAYMENT_PROVIDERS = ['Stripe', 'Silkpay', 'PayPal', 'Airwallex', 'Wise', 'Sonstiges'];

function formatMoney(value) {
  const num = Number(value || 0);
  return `${num.toFixed(2).replace('.', ',')} €`;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('de-DE');
  } catch {
    return value;
  }
}

function getPaymentDate(group) {
  return group?.paid_date || group?.closing_date || null;
}

function getAgentLabel(group) {
  if (!group?.agents || !Array.isArray(group.agents) || group.agents.length === 0) return '-';
  return group.agents.filter(Boolean).join(', ') || '-';
}

function normalizeTxIds(group) {
  const linked = Array.isArray(group?.linked_transaction_ids)
    ? group.linked_transaction_ids.filter(Boolean)
    : [];
  return [...new Set([group?.payment_id, ...linked].filter(Boolean))];
}

function RefundGroupModal({ group, onClose, onSaved }) {
  const [provider, setProvider] = useState(group?.payment_provider || '');
  const [paymentDate, setPaymentDate] = useState(group?.paid_date || group?.closing_date || '');
  const [txIdsText, setTxIdsText] = useState(normalizeTxIds(group).join(' '));
  const [savingHeader, setSavingHeader] = useState(false);
  const [resettingBlock, setResettingBlock] = useState(false);

  useEffect(() => {
    setProvider(group?.payment_provider || '');
    setPaymentDate(group?.paid_date || group?.closing_date || '');
    setTxIdsText(normalizeTxIds(group).join(' '));
  }, [group]);

  if (!group) return null;

  const txIds = normalizeTxIds(group);

  const saveHeader = async () => {
    try {
      setSavingHeader(true);
      const ids = (group.orders || []).map((o) => o.id);

      await API.patch('/refunds/assign-transaction', {
        ids,
        payment_ids: txIdsText,
        payment_provider: provider || 'Sonstiges',
        paid_date: paymentDate || null
      });

      onSaved();
      onClose();
    } catch (err) {
      console.error('Refund Header speichern fehlgeschlagen', err);
      alert('Speichern fehlgeschlagen');
    } finally {
      setSavingHeader(false);
    }
  };

  const resetTransactionBlock = async () => {
    if (!group?.payment_id) {
      alert('Diese Gruppe hat keine Haupt-Transaktions-ID und kann nicht als Block zurückgesetzt werden.');
      return;
    }

    const ok = window.confirm(
      `Transaktion ${group.payment_id} wirklich zurücksetzen?\n\n` +
        'Alle Bestellungen dieses Blocks werden wieder auf Eingereicht gesetzt.\n' +
        'Payment-ID, weitere TransIDs, Provider, Zahlungsdatum und Closing-Date werden entfernt.\n' +
        'Die Beträge werden anhand Preis/Provision neu berechnet.'
    );

    if (!ok) return;

    try {
      setResettingBlock(true);
      const res = await API.post('/refunds/reset-transaction', {
        payment_id: group.payment_id
      });

      alert(`${res.data?.updated || 0} Bestellung(en) wurden zurückgesetzt.`);
      onSaved();
      onClose();
    } catch (err) {
      console.error('Transaktionsblock zurücksetzen fehlgeschlagen', err);
      alert('Zurücksetzen fehlgeschlagen: ' + (err.response?.data?.error || err.message));
    } finally {
      setResettingBlock(false);
    }
  };

  const saveNotes = async (orderId, notes) => {
    await API.patch('/refunds/update-notes', {
      order_id: orderId,
      notes
    });
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalBox}>
        <div style={styles.modalHeader}>
          <div>
            <div style={styles.modalTitle}>Erstattung bearbeiten</div>
            <div style={styles.modalSubtitle}>
              Haupt-TransID:{' '}
              <span style={{ fontFamily: 'DM Mono', color: 'var(--accent)' }}>
                {group.payment_id || '-'}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="btn btn-ghost" style={{ padding: '8px 10px' }}>
            <X size={16} />
          </button>
        </div>

        <div style={styles.modalInfoGrid}>
          <div style={styles.infoCard}>
            <div style={styles.infoLabel}>Payment-Datum</div>
            <input
              type="date"
              value={paymentDate || ''}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>

          <div style={styles.infoCard}>
            <div style={styles.infoLabel}>Provider</div>
            <select value={provider} onChange={(e) => setProvider(e.target.value)}>
              <option value="">— wählen —</option>
              {PAYMENT_PROVIDERS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.infoCard}>
            <div style={styles.infoLabel}>Agent</div>
            <div style={styles.infoValue}>{getAgentLabel(group)}</div>
          </div>

          <div style={styles.infoCard}>
            <div style={styles.infoLabel}>Gesamtbetrag</div>
            <div style={{ ...styles.infoValue, color: 'var(--accent2)', fontWeight: 700 }}>
              {formatMoney(group.total)}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={styles.fieldLabel}>Transaktions-IDs</label>
          <input
            value={txIdsText}
            onChange={(e) => setTxIdsText(e.target.value)}
            placeholder="IDs mit Leerzeichen trennen"
            style={{ fontFamily: 'DM Mono' }}
          />
          <div style={styles.helperText}>
            Erste ID = Haupt-TransID. Weitere IDs werden als linked_transaction_ids gespeichert.
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={styles.subBlockTitle}>Weitere TransIDs</div>
          <div style={styles.txBadgeWrap}>
            {txIds.length > 0 ? (
              txIds.map((id) => (
                <span key={id} style={styles.txBadge}>
                  {id}
                </span>
              ))
            ) : (
              <span style={{ color: 'var(--text3)', fontSize: 12 }}>Keine</span>
            )}
          </div>
        </div>

        <div style={styles.tableWrap}>
          <table>
            <thead>
              <tr>
                <th>Bestellnummer</th>
                <th>Produkt</th>
                <th>Agent</th>
                <th>Order-Datum</th>
                <th>Betrag</th>
                <th>Notiz</th>
              </tr>
            </thead>
            <tbody>
              {(group.orders || []).map((order) => (
                <RefundOrderRow key={order.id} order={order} onSaveNotes={saveNotes} />
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.modalActions}>
          <button
            className="btn btn-ghost"
            onClick={resetTransactionBlock}
            disabled={resettingBlock || !group.payment_id}
            style={{ color: '#f59e0b', borderColor: 'rgba(245,158,11,0.35)' }}
          >
            {resettingBlock ? 'Setze zurück...' : 'Block zurücksetzen'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>
            Schließen
          </button>
          <button className="btn btn-primary" onClick={saveHeader} disabled={savingHeader}>
            <Save size={14} />
            {savingHeader ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RefundOrderRow({ order, onSaveNotes }) {
  const [notes, setNotes] = useState(order.notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setNotes(order.notes || '');
  }, [order.notes]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaved(false);
      await onSaveNotes(order.id, notes);
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
    } catch (err) {
      console.error('Notes speichern fehlgeschlagen', err);
      alert('Notiz konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td style={{ fontFamily: 'DM Mono', fontSize: 11 }}>{order.order_number}</td>
      <td style={{ maxWidth: 260 }}>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {order.product_name || '-'}
        </div>
      </td>
      <td>{order.agent_name || '-'}</td>
      <td>{formatDate(order.order_date)}</td>
      <td style={{ color: 'var(--accent2)', fontWeight: 600 }}>{formatMoney(order.betrag)}</td>
      <td style={{ minWidth: 340 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notiz"
            rows={2}
            style={{
              width: '100%',
              resize: 'vertical',
              fontSize: 12,
              fontFamily: 'DM Mono',
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'var(--bg3)',
              color: 'var(--text)'
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-ghost"
              onClick={handleSave}
              disabled={saving}
              style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}
            >
              {saving ? '...' : saved ? '✓ Gespeichert' : 'OK'}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

function RefundCreateModal({ onClose, onDone, initialData = null }) {
  const [text, setText] = useState('');
  const [paymentId, setPaymentId] = useState(initialData?.paymentIdText || '');
  const [paidDate, setPaidDate] = useState(
    initialData?.paidDate || new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [editRows, setEditRows] = useState([]);
  const [savingRow, setSavingRow] = useState(null);
  const [savedRows, setSavedRows] = useState(new Set());
  const [lastRefund, setLastRefund] = useState(null);
  const [tatsaechlich, setTatsaechlich] = useState(
    initialData?.actualAmount != null ? String(initialData.actualAmount) : ''
  );
  const [nachzahlungLabel, setNachzahlungLabel] = useState('');
  const [inboxEntries, setInboxEntries] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [agentOptions, setAgentOptions] = useState([]);
  const [selectedInboxEntryId, setSelectedInboxEntryId] = useState(null);

useEffect(() => {
  if (!initialData) return;

 

  setPaymentId(initialData.paymentIdText || '');
  setPaidDate(initialData.paidDate || new Date().toISOString().split('T')[0]);
  setTatsaechlich(
    initialData.actualAmount != null ? String(initialData.actualAmount) : ''
  );
  setSelectedAgentId(initialData.agentId || '');
}, [initialData]);

  useEffect(() => {
    API.get('/refunds/last-refund')
      .then((res) => {
        if (res.data.lastRefund) setLastRefund(res.data.lastRefund);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    API.get('/agents')
      .then((res) => {
        setAgentOptions(res.data?.agents || []);
      })
      .catch(() => {
        setAgentOptions([]);
      });
  }, []);

  useEffect(() => {
  if (!initialData) return;

  setPaymentId(initialData.paymentIdText || '');
  setPaidDate(initialData.paidDate || new Date().toISOString().split('T')[0]);
  setTatsaechlich(
    initialData.actualAmount != null ? String(initialData.actualAmount) : ''
  );
  setSelectedAgentId(initialData.agentId || '');
}, [initialData]);

  useEffect(() => {
    if (!selectedAgentId) {
      setInboxEntries([]);
      return;
    }

    setLoadingInbox(true);
    API.get('/refund-inbox/open', {
      params: { agent_id: selectedAgentId }
    })
      .then((res) => {
        setInboxEntries(res.data?.entries || []);
      })
      .catch(() => {
        setInboxEntries([]);
      })
      .finally(() => {
        setLoadingInbox(false);
      });
  }, [selectedAgentId]);

  const parseNrs = (raw) => {
    const matches = String(raw || '').match(/\d{3}-\d{7}-\d{7}/g) || [];
    return [...new Set(matches)];
  };

  const submit = async () => {
    const numbers = parseNrs(text);
    if (!numbers.length) {
      alert('Keine gültigen Bestellnummern erkannt!');
      return;
    }

    if (!paymentId.trim()) {
      const ok = window.confirm('Keine Transaktions-ID eingetragen. Trotzdem fortfahren?');
      if (!ok) return;
    }

    setLoading(true);
    try {
        const res = await API.post('/refunds/bulk-refund', {
        order_numbers: numbers,
        payment_id: paymentId,
        paid_date: paidDate
      });

      if (selectedInboxEntryId) {
        try {
          await API.patch(`/refund-inbox/${selectedInboxEntryId}/use`);
          setInboxEntries((prev) => prev.filter((e) => e.id !== selectedInboxEntryId));
        } catch (e) {
          console.error('Refund-Block konnte nicht auf used gesetzt werden', e);
        }
      }

      setResult(res.data);
      onDone();
      const allNumbers = [...(res.data.updated || []), ...(res.data.already_refunded || [])];
      if (allNumbers.length > 0) {
        const orderDetails = [];
        for (const nr of allNumbers) {
          try {
            const r = await API.get('/orders', { params: { search: nr, limit: 1 } });
            const found = r.data.orders?.[0];
            if (found) {
              orderDetails.push({
                id: found.id,
                order_number: found.order_number,
                order_date: found.order_date,
                tax_ref: !!found.tax_ref,
                req_type: found.req_type || 'Text',
                betrag: parseFloat(found.betrag || 0),
                price: parseFloat(found.price || 0),
                commission: parseFloat(found.commission || 25),
                notes: found.notes || '',
                status: found.status || 'Erstattet',
                is_storno: found.status === 'Storno',
                alreadyRefunded: (res.data.already_refunded || []).includes(nr)
              });
            }
          } catch {}
        }
        setEditRows(orderDetails);
      }
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const recalcBetrag = (row) => {
    const price = parseFloat(row.price) || 0;
    const commission = parseFloat(row.commission) || 0;
    const taxRef = !!row.tax_ref;
    return taxRef ? parseFloat((price * 0.35 + commission).toFixed(2)) : commission;
  };

  const updateRow = (id, field, value) => {
    setEditRows((rows) =>
      rows.map((r) => {
        if (r.id !== id) return r;

        const updated = { ...r, [field]: value };

        if (updated.is_storno) {
          updated.betrag = 0;
        } else if (field === 'tax_ref' || field === 'commission' || field === 'price') {
          updated.betrag = recalcBetrag(updated);
        }

        return updated;
      })
    );
  };

  const saveRow = async (row) => {
    setSavingRow(row.id);
    try {
      await API.patch('/orders/bulk-update', {
        ids: [row.id],
        fields: {
          tax_ref: row.tax_ref,
          req_type: row.req_type,
          betrag: row.is_storno ? 0 : (parseFloat(row.betrag) || 0),
          commission: parseFloat(row.commission) || 0,
          price: parseFloat(row.price) || 0,
          status: row.is_storno ? 'Storno' : 'Erstattet'
        }
      });
      setSavedRows((prev) => new Set([...prev, row.id]));
    } catch (err) {
      alert('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingRow(null);
    }
  };

  const toggleStorno = async (row) => {
    const nextIsStorno = !row.is_storno;
    const updated = {
      ...row,
      is_storno: nextIsStorno,
      betrag: nextIsStorno ? 0 : recalcBetrag({ ...row, is_storno: false })
    };

    setEditRows((rows) => rows.map((r) => (r.id === row.id ? updated : r)));
    await saveRow(updated);
  };

  const saveAllRows = async (rowsToSave) => {
    for (const row of rowsToSave) {
      await saveRow(row);
    }
  };

  const totalBetrag = editRows.reduce((s, r) => s + (parseFloat(r.betrag) || 0), 0);
  const tatsaechlichNum = parseFloat(tatsaechlich || 0) || 0;
  const differenz =
    tatsaechlich === '' ? 0 : parseFloat((tatsaechlichNum - totalBetrag).toFixed(2));

  const fmt = (v) => parseFloat(v || 0).toFixed(2).replace('.', ',') + ' €';

  const proportionalVerteilen = async () => {
    if (!editRows.length) {
      alert('Keine Zeilen vorhanden');
      return;
    }

    if (tatsaechlich === '') {
      alert('Bitte zuerst den tatsächlich erhaltenen Betrag eintragen');
      return;
    }

    const activeRows = editRows.filter((row) => !row.is_storno);

    if (activeRows.length === 0) {
      alert('Verteilung nicht möglich, weil alle Zeilen auf Storno stehen');
      return;
    }

    const currentTotal = activeRows.reduce((sum, row) => sum + (parseFloat(row.betrag) || 0), 0);
    const targetTotal = parseFloat(tatsaechlich) || 0;
    const diff = parseFloat((targetTotal - currentTotal).toFixed(2));

    if (Math.abs(diff) < 0.01) {
      setNachzahlungLabel('Keine Verteilung nötig');
      return;
    }

    if (currentTotal <= 0) {
      alert('Verteilung nicht möglich, weil die aktuelle Summe 0 ist');
      return;
    }

    let running = 0;

    const lastActiveId = activeRows[activeRows.length - 1].id;

    const updatedRows = editRows.map((row) => {
      if (row.is_storno) {
        return { ...row, betrag: 0 };
      }

      const current = parseFloat(row.betrag) || 0;

      if (row.id === lastActiveId) {
        const lastValue = parseFloat((current + (diff - running)).toFixed(2));
        return { ...row, betrag: lastValue };
      }

      const anteil = current / currentTotal;
      const add = parseFloat((diff * anteil).toFixed(2));
      running += add;

      return {
        ...row,
        betrag: parseFloat((current + add).toFixed(2))
      };
    });

    setEditRows(updatedRows);
    setNachzahlungLabel(
      diff > 0
        ? `Nachzahlung von ${fmt(diff)} proportional verteilt`
        : `Abzug von ${fmt(Math.abs(diff))} proportional verteilt`
    );

    await saveAllRows(updatedRows);
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={{ ...styles.modalBox, width: result ? 1120 : 560 }}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Bestellungen erstatten</h2>
          <button onClick={onClose} className="btn btn-ghost" style={{ padding: '8px 10px' }}>
            <X size={16} />
          </button>
        </div>

        {lastRefund && !result && (
          <div
            style={{
              marginBottom: 16,
              padding: '8px 14px',
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              borderRadius: 8
            }}
          >
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>Zuletzt erstattet: </span>
            <span
              style={{
                fontFamily: 'DM Mono',
                fontSize: 12,
                color: 'var(--accent)',
                fontWeight: 600
              }}
            >
              {lastRefund.note}
            </span>
          </div>
        )}

        {!result ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={styles.fieldLabel}>Bestellnummern</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={8}
                placeholder={
                  '302-1234567-1234567\n305-7654321-7654321\n\nOder kompletter Text – Bestellnummern werden automatisch erkannt'
                }
                style={{
                  width: '100%',
                  resize: 'vertical',
                  fontFamily: 'DM Mono',
                  fontSize: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--bg3)',
                  color: 'var(--text)'
                }}
              />
              {text && (
                <p style={{ color: 'var(--accent)', fontSize: 12, marginTop: 4 }}>
                  ✓ {parseNrs(text).length} gültige Bestellnummer(n) erkannt
                </p>
              )}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 12,
                marginBottom: 24
              }}
            >
              <div>
                <label style={styles.fieldLabel}>Transaktions-ID (optional)</label>
                <input value={paymentId} onChange={(e) => setPaymentId(e.target.value)} />
              </div>
              <div>
                <label style={styles.fieldLabel}>Payment-Datum</label>
                <input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} />
              </div>
              <div>
                <label style={styles.fieldLabel}>Agent</label>
                <select
                  value={selectedAgentId}
                  onChange={(e) => setSelectedAgentId(e.target.value)}
                >
                  <option value="">Agent wählen</option>
                  {agentOptions.map((a) => (
                    <option key={a.id} value={a.agent_id}>
                      {a.display_name} ({a.agent_id})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              style={{
                marginBottom: 20,
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: 14
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text2)',
                  fontFamily: 'Syne',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  marginBottom: 10
                }}
              >
                Refund-Blöcke
              </div>

              {!selectedAgentId ? (
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                  Bitte zuerst einen Agenten auswählen.
                </div>
              ) : loadingInbox ? (
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Lade Blöcke...</div>
              ) : inboxEntries.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                  Keine offenen Refund-Blöcke für diesen Agenten gefunden
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {inboxEntries.map((entry) => {
                    const diff =
                      tatsaechlich === ''
                        ? null
                        : parseFloat(
                            (
                              parseFloat(tatsaechlich || 0) - parseFloat(entry.system_total || 0)
                            ).toFixed(2)
                          );

                    const hasWarning = !entry.is_valid || !!entry.warning_text;

                    return (
                      <button
                        key={entry.id}
                        type="button"
                        onClick={() => {
  			  setText((entry.order_numbers || []).join('\n'));
  			  setSelectedInboxEntryId(entry.id);
			}}
                        style={{
                          border: hasWarning
                            ? '1px solid rgba(245,158,11,0.45)'
                            : '1px solid var(--border)',
                          borderRadius: 8,
                          padding: '10px 12px',
                          background: hasWarning ? 'rgba(245,158,11,0.08)' : 'var(--bg2)',
                          textAlign: 'left',
                          cursor: 'pointer'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 12,
                            alignItems: 'center'
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                              {entry.agent_name || 'Unbekannter Agent'}
                            </div>

                            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                              {entry.matched_count || 0} von {entry.parsed_count || 0} Nummern ·
                              Summe {formatMoney(entry.system_total || 0)}
                              {diff !== null &&
                                ` · Differenz ${diff >= 0 ? '+' : ''}${formatMoney(diff)}`}
                            </div>

                            <div
                              style={{
                                fontSize: 11,
                                color: 'var(--text3)',
                                marginTop: 2,
                                fontFamily: 'DM Mono'
                              }}
                            >
                              {(entry.order_numbers || []).slice(0, 3).join(', ')}
                              {(entry.order_numbers || []).length > 3 ? ' ...' : ''}
                            </div>

                            {entry.warning_text && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: '#f59e0b',
                                  marginTop: 6,
                                  fontWeight: 600
                                }}
                              >
                                Warnung: {entry.warning_text}
                              </div>
                            )}
                          </div>

                          <div
                            style={{
                              whiteSpace: 'nowrap',
                              fontSize: 12,
                              fontWeight: 700,
                              color: 'var(--accent)'
                            }}
                          >
                            Übernehmen
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-ghost" onClick={onClose}>
                Abbrechen
              </button>
              <button className="btn btn-primary" onClick={submit} disabled={loading || !text}>
                <DollarSign size={14} />
                {loading ? 'Verarbeite...' : 'Erstatten'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 12,
                marginBottom: 20
              }}
            >
              <div
                style={{
                  background: 'rgba(74,222,128,0.1)',
                  border: '1px solid rgba(74,222,128,0.3)',
                  borderRadius: 10,
                  padding: '14px 16px'
                }}
              >
                <div style={styles.infoLabel}>Gesamtbetrag</div>
                <div
                  style={{
                    fontFamily: 'Syne',
                    fontWeight: 800,
                    fontSize: 22,
                    color: '#4ade80'
                  }}
                >
                  {fmt(totalBetrag)}
                </div>
              </div>
              <div style={styles.infoCard}>
                <div style={styles.infoLabel}>Transaktions-ID</div>
                <div style={styles.infoValue}>{paymentId || '—'}</div>
              </div>
              <div style={styles.infoCard}>
                <div style={styles.infoLabel}>Payment-Datum</div>
                <div style={styles.infoValue}>{paidDate}</div>
              </div>
            </div>

            <div
              style={{
                marginBottom: 18,
                padding: 14,
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 10
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text2)',
                  fontFamily: 'Syne',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  marginBottom: 10
                }}
              >
                Zahlungsausgleich
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '180px 180px 1fr auto',
                  gap: 12,
                  alignItems: 'end'
                }}
              >
                <div>
                  <label style={styles.fieldLabel}>Berechnete Summe</label>
                  <div
                    style={{
                      height: 38,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border)',
                      background: 'var(--bg2)',
                      fontFamily: 'DM Mono',
                      color: 'var(--accent2)',
                      fontWeight: 700
                    }}
                  >
                    {fmt(totalBetrag)}
                  </div>
                </div>

                <div>
                  <label style={styles.fieldLabel}>Tatsächlich erhalten</label>
                  <input
                    type="number"
                    step="0.01"
                    value={tatsaechlich}
                    onChange={(e) => setTatsaechlich(e.target.value)}
                    placeholder="z. B. 127.50"
                    style={{ fontFamily: 'DM Mono' }}
                  />
                </div>

                <div>
                  <label style={styles.fieldLabel}>Differenz</label>
                  <div
                    style={{
                      height: 38,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border)',
                      background: 'var(--bg2)',
                      fontFamily: 'DM Mono',
                      color:
                        tatsaechlich === ''
                          ? 'var(--text3)'
                          : differenz >= 0
                            ? '#4ade80'
                            : '#f59e0b',
                      fontWeight: 700
                    }}
                  >
                    {tatsaechlich === '' ? '—' : `${differenz >= 0 ? '+' : ''}${fmt(differenz)}`}
                  </div>
                </div>

                <button className="btn btn-primary" onClick={proportionalVerteilen}>
                  Proportional verteilen
                </button>
              </div>

              {nachzahlungLabel && (
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--accent)' }}>
                  {nachzahlungLabel}
                </div>
              )}
            </div>

            {result.not_found?.length > 0 && (
              <div
                style={{
                  marginBottom: 16,
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.2)',
                  borderRadius: 8,
                  padding: '10px 14px'
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: '#ef4444',
                    fontWeight: 700,
                    fontFamily: 'Syne',
                    marginBottom: 6
                  }}
                >
                  Nicht gefunden ({result.not_found.length})
                </div>
                {result.not_found.map((n) => (
                  <div
                    key={n}
                    style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono' }}
                  >
                    {n}
                  </div>
                ))}
              </div>
            )}

            {editRows.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text2)',
                    fontFamily: 'Syne',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    marginBottom: 10
                  }}
                >
                  Bestellungen prüfen & anpassen
                </div>

                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 80px 1fr 80px 140px 100px 100px 100px 80px 32px',
                      padding: '8px 12px',
                      background: 'var(--bg3)',
                      borderBottom: '1px solid var(--border)'
                    }}
                  >
                    {['Bestellt', 'Bezahlt', 'Bestellnummer', 'Provi', 'Typ', 'Preis', 'Provision', 'Betrag', 'Storno', ''].map(
                      (h, i) => (
                        <div
                          key={i}
                          style={{
                            fontSize: 10,
                            color: 'var(--text3)',
                            fontFamily: 'Syne',
                            textTransform: 'uppercase',
                            fontWeight: 700
                          }}
                        >
                          {h}
                        </div>
                      )
                    )}
                  </div>

                  {editRows.map((row) => {
                    const isSaved = savedRows.has(row.id);
                    const isSaving = savingRow === row.id;

                    return (
                      <div
                        key={row.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '80px 80px 1fr 80px 140px 100px 100px 100px 80px 32px',
                          padding: '7px 12px',
                          borderBottom: '1px solid var(--border)',
                          alignItems: 'center',
                          background: row.is_storno
                            ? 'rgba(239,68,68,0.08)'
                            : row.alreadyRefunded
                              ? 'rgba(251,191,36,0.05)'
                              : isSaved
                                ? 'rgba(74,222,128,0.04)'
                                : 'transparent'
                        }}
                      >
                        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono' }}>
                          {row.order_date ? new Date(row.order_date).toLocaleDateString('de-DE') : '—'}
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--accent2)',
                            fontFamily: 'DM Mono',
                            fontWeight: 600
                          }}
                        >
                          {paidDate ? new Date(paidDate).toLocaleDateString('de-DE') : '—'}
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text2)',
                            fontFamily: 'DM Mono',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {row.order_number}
                        </div>

                        <div style={{ display: 'flex', gap: 3 }}>
                          {[true, false].map((v) => (
                            <button
                              key={String(v)}
                              disabled={row.is_storno}
                              onClick={async () => {
                                const updated = { ...row, tax_ref: v };
                                updated.betrag = recalcBetrag(updated);
                                setEditRows((rows) => rows.map((r) => (r.id === row.id ? updated : r)));
                                await saveRow(updated);
                              }}
                              style={{
                                background:
                                  row.tax_ref === v ? (v ? 'var(--accent2)' : '#6b728033') : 'transparent',
                                color: row.tax_ref === v ? (v ? '#fff' : 'var(--text2)') : 'var(--text3)',
                                border: `1px solid ${
                                  row.tax_ref === v ? (v ? 'var(--accent2)' : 'var(--border)') : 'var(--border)'
                                }`,
                                borderRadius: 4,
                                padding: '2px 6px',
                                fontSize: 10,
                                cursor: 'pointer',
                                fontWeight: 600
                              }}
                            >
                              {v ? 'Ja' : 'Nein'}
                            </button>
                          ))}
                        </div>

                        <div style={{ display: 'flex', gap: 3 }}>
                          {['Text', 'Bild', 'Video'].map((t) => (
                            <button
                              key={t}
                              disabled={row.is_storno}
                              onClick={async () => {
                                const updated = { ...row, req_type: t };
                                setEditRows((rows) => rows.map((r) => (r.id === row.id ? updated : r)));
                                await saveRow(updated);
                              }}
                              style={{
                                background: row.req_type === t ? 'var(--accent)' : 'transparent',
                                color: row.req_type === t ? '#fff' : 'var(--text3)',
                                border: `1px solid ${row.req_type === t ? 'var(--accent)' : 'var(--border)'}`,
                                borderRadius: 4,
                                padding: '2px 6px',
                                fontSize: 10,
                                cursor: 'pointer',
                                fontWeight: 600
                              }}
                            >
                              {t}
                            </button>
                          ))}
                        </div>

                        <input
                          type="number"
                          disabled={row.is_storno}
                          value={row.price}
                          onChange={(e) => updateRow(row.id, 'price', parseFloat(e.target.value) || 0)}
                          onBlur={async () => {
                            const current = editRows.find((r) => r.id === row.id);
                            if (current) await saveRow(current);
                          }}
                          step="0.01"
                          style={{
                            fontFamily: 'DM Mono',
                            fontSize: 12,
                            padding: '3px 8px',
                            width: '100%',
                            textAlign: 'right'
                          }}
                        />

                        <input
                          type="number"
                          disabled={row.is_storno}
                          value={row.commission}
                          onChange={(e) => updateRow(row.id, 'commission', parseFloat(e.target.value) || 0)}
                          onBlur={async () => {
                            const current = editRows.find((r) => r.id === row.id);
                            if (current) await saveRow(current);
                          }}
                          step="0.01"
                          style={{
                            fontFamily: 'DM Mono',
                            fontSize: 12,
                            padding: '3px 8px',
                            width: '100%',
                            textAlign: 'right'
                          }}
                        />

                        <input
                          type="number"
                          disabled={row.is_storno}
                          value={row.betrag}
                          onChange={(e) => updateRow(row.id, 'betrag', parseFloat(e.target.value) || 0)}
                          onBlur={async (e) => {
                            const val = parseFloat(e.target.value) || 0;
                            const current = editRows.find((r) => r.id === row.id);
                            if (!current) return;

                            const updated = { ...current, betrag: val };
                            setEditRows((rows) => rows.map((r) => (r.id === row.id ? updated : r)));
                            await saveRow(updated);
                          }}
                          step="0.01"
                          style={{
                            fontFamily: 'DM Mono',
                            fontSize: 12,
                            padding: '3px 8px',
                            width: '100%',
                            textAlign: 'right',
                            color: 'var(--accent2)',
                            fontWeight: 600
                          }}
                        />

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                              fontSize: 10,
                              color: row.is_storno ? '#ef4444' : 'var(--text3)',
                              fontWeight: row.is_storno ? 700 : 500,
                              cursor: 'pointer'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!row.is_storno}
                              onChange={() => toggleStorno(row)}
                            />
                            Storno
                          </label>
                        </div>

                        <div style={{ fontSize: 14, textAlign: 'center' }}>
                          {isSaving ? '⏳' : isSaved ? '✓' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                className="btn btn-ghost"
                onClick={() => {
 		 setResult(null);
	         setEditRows([]);
 		 setSavedRows(new Set());
	         setSelectedInboxEntryId(null);
 	         setTatsaechlich(
		    initialData?.actualAmount != null ? String(initialData.actualAmount) : ''
		  );
		  setNachzahlungLabel('');
		}}    
          >
                ← Zurück
              </button>
              <button className="btn btn-primary" onClick={onClose}>
                <CheckCircle size={14} />
                Erledigt
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Refunds() {
  const [grouped, setGrouped] = useState([]);
  const [ungrouped, setUngrouped] = useState([]);
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundModalInitialData, setRefundModalInitialData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [agent, setAgent] = useState('');
  const [provider, setProvider] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (agent) params.agent = agent;
      if (provider) params.provider = provider;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const [refundsRes, agentsRes] = await Promise.all([
        API.get('/refunds', { params }),
        API.get('/refunds/accounts')
      ]);

      setGrouped(refundsRes.data?.grouped || []);
      setUngrouped(refundsRes.data?.ungrouped || []);
      setStats(refundsRes.data?.stats || null);
      setAgents(agentsRes.data?.accounts || []);
    } catch (err) {
      console.error('Refunds laden fehlgeschlagen', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [search, agent, provider, dateFrom, dateTo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 250);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const applyAwxFromHash = () => {
      const hash = window.location.hash || '';
      const queryString = hash.includes('?') ? hash.split('?')[1] : '';
      const params = new URLSearchParams(queryString);

      if (params.get('awx_refund') !== '1') return;

      const paidDate = params.get('paidDate') || '';
      const paymentIdText = params.get('paymentIdText') || '';
      const actualAmount = params.get('actualAmount') || '';
      const agentId = params.get('agentId') || '';

      setRefundModalInitialData({
      paidDate,
      paymentIdText,
      actualAmount,
      agentId
     });
      setShowRefundModal(true);

      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}#/refunds`
      );
    };

    applyAwxFromHash();
    window.addEventListener('hashchange', applyAwxFromHash);

    return () => {
      window.removeEventListener('hashchange', applyAwxFromHash);
    };
  }, []);

  const sortedGrouped = useMemo(() => {
    return [...grouped].sort((a, b) => {
      const ta = new Date(getPaymentDate(a) || 0).getTime();
      const tb = new Date(getPaymentDate(b) || 0).getTime();
      return tb - ta;
    });
  }, [grouped]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Erstattungen</h1>
          <p style={{ color: 'var(--text2)', marginTop: 4, fontSize: 13 }}>
            {sortedGrouped.length} gruppierte Transaktionen · {ungrouped.length} offene Einzelposten
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-primary"
            onClick={() => {
              setRefundModalInitialData(null);
              setShowRefundModal(true);
            }}
          >
            <DollarSign size={14} />
            Bestellungen erstatten
          </button>

          <button className="btn btn-ghost" onClick={() => setShowFilters((v) => !v)}>
            <Filter size={14} />
            Filter
          </button>

          <button className="btn btn-ghost" onClick={loadData}>
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid-4" style={{ marginBottom: 16 }}>
          {[
            {
              label: 'Heute',
              value: formatMoney(stats.today?.total || 0),
              sub: `${stats.today?.count || 0} Bestellungen`
            },
            {
              label: 'Diese Woche',
              value: formatMoney(stats.week?.total || 0),
              sub: `${stats.week?.count || 0} Bestellungen`
            },
            {
              label: 'Dieser Monat',
              value: formatMoney(stats.month?.total || 0),
              sub: `${stats.month?.count || 0} Bestellungen`
            },
            {
              label: 'Gesamt',
              value: formatMoney(stats.all?.total || 0),
              sub: `${stats.all?.count || 0} Bestellungen`
            }
          ].map((s, i) => (
            <div key={i} className="card">
              <div style={styles.statLabel}>{s.label}</div>
              <div style={styles.statValue}>{s.value}</div>
              <div style={styles.statSub}>{s.sub}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ position: 'relative', marginBottom: 12 }}>
        <Search
          size={14}
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text3)'
          }}
        />
        <input
          placeholder="Suche nach Trans-ID, weiterer Trans-ID, Bestellnummer oder Produkt..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ paddingLeft: 36 }}
        />
      </div>

      {showFilters && (
        <div style={styles.filterPanel}>
          <div style={styles.filterGrid}>
            <div>
              <label style={styles.filterLabel}>
                <Calendar size={12} /> Datum von
              </label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>

            <div>
              <label style={styles.filterLabel}>
                <Calendar size={12} /> Datum bis
              </label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>

            <div>
              <label style={styles.filterLabel}>
                <User size={12} /> Agent
              </label>
              <select value={agent} onChange={(e) => setAgent(e.target.value)}>
                <option value="">Alle Agenten</option>
                {agents.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={styles.filterLabel}>
                <CreditCard size={12} /> Provider
              </label>
              <select value={provider} onChange={(e) => setProvider(e.target.value)}>
                <option value="">Alle Provider</option>
                {PAYMENT_PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button
              className="btn btn-ghost"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
                setAgent('');
                setProvider('');
                setSearch('');
                setSearchInput('');
              }}
              type="button"
            >
              Filter zurücksetzen
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 18 }}>
        <div style={styles.blockHeader}>
          <div>
            <div style={styles.blockTitle}>Erstattete Transaktionen</div>
            <div style={styles.blockSub}>Hauptansicht für Verwaltung nach Payment-Datum und Trans-ID</div>
          </div>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Lade gruppierte Erstattungen...</div>
        ) : sortedGrouped.length === 0 ? (
          <div style={styles.emptyState}>Keine gruppierten Erstattungen gefunden</div>
        ) : (
          <div style={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Payment-Datum</th>
                  <th>Transaktions-ID</th>
                  <th>Agent</th>
                  <th>Anzahl</th>
                  <th>Betrag</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedGrouped.map((group) => (
                  <tr
                    key={`${group.payment_id}-${group.payment_provider || ''}`}
                    onClick={() => setSelectedGroup(group)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{formatDate(getPaymentDate(group))}</td>
                    <td style={{ fontFamily: 'DM Mono', fontSize: 11, color: 'var(--accent)' }}>
                      {group.payment_id || '-'}
                    </td>
                    <td>{getAgentLabel(group)}</td>
                    <td>{group.count || 0}</td>
                    <td style={{ color: 'var(--accent2)', fontWeight: 700 }}>
                      {formatMoney(group.total)}
                    </td>
                    <td style={{ width: 40, textAlign: 'right', color: 'var(--text3)' }}>
                      <ChevronRight size={14} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={styles.blockHeader}>
          <div>
            <div style={styles.blockTitle}>Offene Erstattungen</div>
            <div style={styles.blockSub}>Erstattet, aber noch ohne Haupt-TransID</div>
          </div>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Lade offene Erstattungen...</div>
        ) : ungrouped.length === 0 ? (
          <div style={styles.emptyState}>Keine offenen Erstattungen vorhanden</div>
        ) : (
          <div style={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Order-Datum</th>
                  <th>Bestellnummer</th>
                  <th>Agent</th>
                  <th>Produkt</th>
                  <th>Betrag</th>
                </tr>
              </thead>
              <tbody>
                {ungrouped.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDate(row.order_date)}</td>
                    <td style={{ fontFamily: 'DM Mono', fontSize: 11 }}>{row.order_number}</td>
                    <td>{row.agent_name || '-'}</td>
                    <td style={{ maxWidth: 320 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.product_name || '-'}
                      </div>
                    </td>
                    <td style={{ color: 'var(--accent2)', fontWeight: 700 }}>
                      {formatMoney(row.betrag)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showRefundModal && (
        <RefundCreateModal
          onClose={() => setShowRefundModal(false)}
          onDone={loadData}
          initialData={refundModalInitialData}
        />
      )}

      {selectedGroup && (
        <RefundGroupModal group={selectedGroup} onClose={() => setSelectedGroup(null)} onSaved={loadData} />
      )}
    </div>
  );
}

const styles = {
  statLabel: {
    color: 'var(--text3)',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontFamily: 'Syne',
    marginBottom: 8
  },
  statValue: {
    fontSize: 28,
    fontFamily: 'Syne',
    fontWeight: 800,
    color: 'var(--accent2)'
  },
  statSub: {
    color: 'var(--text3)',
    fontSize: 12,
    marginTop: 4
  },
  filterPanel: {
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: 16,
    marginBottom: 16
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 12
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
    color: 'var(--text2)',
    fontSize: 11,
    fontFamily: 'Syne',
    textTransform: 'uppercase'
  },
  blockHeader: {
    padding: '16px 18px 12px',
    borderBottom: '1px solid var(--border)'
  },
  blockTitle: {
    fontFamily: 'Syne',
    fontSize: 16,
    fontWeight: 800,
    color: 'var(--text)'
  },
  blockSub: {
    marginTop: 4,
    fontSize: 12,
    color: 'var(--text3)'
  },
  tableWrap: {
    overflowX: 'auto'
  },
  emptyState: {
    padding: 40,
    textAlign: 'center',
    color: 'var(--text3)'
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(4px)'
  },
  modalBox: {
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    width: 'min(1320px, 94vw)',
    maxHeight: '92vh',
    overflow: 'auto',
    padding: 24
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20
  },
  modalTitle: {
    fontFamily: 'Syne',
    fontSize: 18,
    fontWeight: 800
  },
  modalSubtitle: {
    marginTop: 4,
    color: 'var(--text3)',
    fontSize: 12
  },
  modalInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12,
    marginBottom: 16
  },
  infoCard: {
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: 12
  },
  infoLabel: {
    color: 'var(--text3)',
    fontSize: 11,
    textTransform: 'uppercase',
    fontFamily: 'Syne',
    marginBottom: 6
  },
  infoValue: {
    color: 'var(--text)',
    fontSize: 13,
    fontWeight: 600
  },
  fieldLabel: {
    display: 'block',
    marginBottom: 6,
    color: 'var(--text2)',
    fontSize: 11,
    fontFamily: 'Syne',
    textTransform: 'uppercase'
  },
  helperText: {
    marginTop: 4,
    color: 'var(--text3)',
    fontSize: 11
  },
  subBlockTitle: {
    fontFamily: 'Syne',
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--text2)',
    textTransform: 'uppercase',
    marginBottom: 8
  },
  txBadgeWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6
  },
  txBadge: {
    fontFamily: 'DM Mono',
    fontSize: 11,
    color: 'var(--accent)',
    background: 'rgba(99,102,241,0.1)',
    border: '1px solid rgba(99,102,241,0.25)',
    borderRadius: 999,
    padding: '4px 10px'
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 18
  }
};